import { useBalancedMarkdown } from '@littlebird/markdown-utils';
import { useEffect, useMemo, useRef } from 'react';

import { useCallbackRef } from '@/hooks/useCallbackRef';
import { trackEvent } from '@/utils/analytics';
import { logChatRenderDebug } from '@/utils/chat-render-debug';

import { buildCopyableParseFailurePayload } from './copyable-telemetry';
import { Markdown } from './Markdown';
import {
  type SmoothingOptions,
  useSmoothedContent,
} from './useSmoothedContent';

export interface SmoothdownProps extends Omit<SmoothingOptions, 'enabled'> {
  id: string;
  className?: string;
  content: string;
  smooth?: boolean;
  autoBalance?: boolean;
  dir?: 'ltr' | 'rtl';
  enableCopyableBlocks?: boolean;
  copyableBlocksEditable?: boolean;
  onCopyableEdit?: (segmentIndex: number, newText: string) => void;
  onAskSelection?: (selection: { text: string }) => void;
  telemetryThreadId?: string;
  telemetryMessageId?: string;
  isCompact?: boolean;
  /** Chat this content belongs to; without it, inline entities like action cards don't render */
  chatId?: string;
}

export function Smoothdown({
  id,
  content,
  smooth = true,
  autoBalance,
  adoptInitialContent = false,
  charactersPerFrame = 3,
  variableCharactersPerFrame,
  className,
  flushOnDisable = false,
  flushDebounceMs,
  onAnimationStart,
  onAnimationComplete,
  dir = 'ltr',
  enableCopyableBlocks = false,
  copyableBlocksEditable = true,
  onCopyableEdit,
  onAskSelection,
  telemetryThreadId,
  telemetryMessageId,
  isCompact = false,
  chatId,
}: SmoothdownProps) {
  logChatRenderDebug(
    'smoothdown-render',
    {
      autoBalance,
      contentHash: `${content.length}:${content.slice(0, 24)}:${content.slice(-24)}`,
      contentLength: content.length,
      enableCopyableBlocks,
      id,
      smooth,
      telemetryMessageId,
      telemetryThreadId,
    },
    id
  );

  const handleCopyableEdit = useCallbackRef(onCopyableEdit);
  const handleAskSelection = useCallbackRef(onAskSelection);

  const smoothed = useSmoothedContent(content, {
    enabled: smooth,
    adoptInitialContent,
    charactersPerFrame,
    variableCharactersPerFrame,
    flushOnDisable,
    flushDebounceMs,
    onAnimationStart,
    onAnimationComplete,
  });

  const balanced = useBalancedMarkdown(smoothed, {
    autoBalance,
    autoCloseCodeBlocks: false,
    resetKey: id,
  });

  logChatRenderDebug(
    'smoothdown-output',
    {
      balancedHash: `${balanced.length}:${balanced.slice(0, 24)}:${balanced.slice(-24)}`,
      balancedLength: balanced.length,
      id,
      smoothedHash: `${smoothed.length}:${smoothed.slice(0, 24)}:${smoothed.slice(-24)}`,
      smoothedLength: smoothed.length,
    },
    id
  );

  const copyableParseFailurePayload = useMemo(() => {
    if (
      !enableCopyableBlocks ||
      !telemetryThreadId ||
      !telemetryMessageId ||
      !balanced.includes('<copyable')
    ) {
      return null;
    }

    return buildCopyableParseFailurePayload({
      threadId: telemetryThreadId,
      messageId: telemetryMessageId,
      smoothdownId: id,
      rawContent: content,
      smoothedContent: smoothed,
      balancedContent: balanced,
    });
  }, [
    balanced,
    content,
    enableCopyableBlocks,
    id,
    smoothed,
    telemetryMessageId,
    telemetryThreadId,
  ]);

  const hasReportedCopyableParseFailure = useRef(false);

  useEffect(() => {
    hasReportedCopyableParseFailure.current = false;
  }, [telemetryMessageId]);

  useEffect(() => {
    if (
      !copyableParseFailurePayload ||
      hasReportedCopyableParseFailure.current
    ) {
      return;
    }

    hasReportedCopyableParseFailure.current = true;
    trackEvent(
      'CHAT_COPYABLE_PARSE_FAILURE',
      copyableParseFailurePayload,
      true
    );
  }, [copyableParseFailurePayload]);

  return (
    <Markdown
      content={balanced}
      id={id}
      className={className}
      dir={dir}
      enableCopyableBlocks={enableCopyableBlocks}
      copyableBlocksEditable={copyableBlocksEditable}
      onCopyableEdit={handleCopyableEdit}
      onAskSelection={handleAskSelection}
      isAnimating={smooth}
      isCompact={isCompact}
      chatId={chatId}
    />
  );
}
